<template>
	<!-- eslint-disable max-lines-->
	<ValidationForm
		#default="{ handleSubmit }"
		tag="div"
		class="cassie-vertical-md"
	>
		<Modal width="700px">
			<template #modal-title>
				<IconButton
					v-if="showBackButton || currentStep > 0"
					class="mr-3"
					data-testid="back-button"
					@click="handleBack"
				>
					mdi-arrow-left
				</IconButton>
				<span class="title">
					{{ modalTitle }}
				</span>
				<v-spacer />
				<!-- Step indicator -->
				<div class="d-flex align-center mx-4">
					<span class="text-caption text--secondary">
						Step {{ currentStep + 1 }} of 2
					</span>
				</div>
				<IconButton
					data-testid="close-button"
					@click="$emit('close')"
				>
					mdi-close
				</IconButton>
			</template>

			<template #modal-content>
				<!-- Step Progress Bar -->
				<div class="step-progress-container mb-4">
					<div class="step-progress-bar">
						<div
							class="step-progress-fill"
							:style="{ width: `${((currentStep + 1) / 2) * 100}%` }"
						/>
					</div>
					<div class="step-labels d-flex justify-space-between">
						<span
							class="text-caption"
							:class="{
								'primary--text font-weight-medium': currentStep === 0,
								'success--text': currentStep > 0,
								'text--secondary': currentStep !== 0 && currentStep <= 0
							}"
						>
							Details
						</span>
						<span
							class="text-caption"
							:class="{
								'primary--text font-weight-medium': currentStep === 1,
								'text--secondary': currentStep !== 1
							}"
						>
							Authentication
						</span>
					</div>
				</div>

				<div class="form-container">
					<!-- Step 1: Details -->
					<div
						v-if="currentStep === 0"
						class="step-content"
					>
						<div class="d-flex flex-column">
							<TextField
								v-model="systemIntegration.integrationSystemName"
								label="System Name *"
								rules="required"
								explainer="Enter a name for this system"
								:disabled="!userFullPermissions"
								class="mb-4"
							/>
							<TextArea
								v-model="systemIntegration.notes"
								label="Description"
								explainer="Enter any explanatory notes to help describe this system (internal use only)"
								:disabled="!userFullPermissions"
								rows="4"
								class="mb-4"
							/>
							<TextField
								v-model="systemIntegration.endpointAddress"
								label="API Base URL"
								:rules="endpointAddressRules"
								explainer="Only required if you choose API authentication in Step 2. Leave blank for Direct to S3. Example: https://api.example.com  (no path or trailing slash)"
								:disabled="!userFullPermissions"
								class="mb-4"
							/>
							<div
								class="d-flex align-center"
								style="gap: 16px;"
							>
								<TextField
									v-model="systemIntegration.frequencyInSeconds"
									label="Connection Frequency *"
									type="number"
									:rules="'required|min_value:1'"
									explainer="Choose the desired frequency for transfer of information to the target system. This period is specified in seconds."
									:disabled="!userFullPermissions"
									style="width: 50%; flex-shrink: 0;"
								/>
							</div>
						</div>
					</div>

					<!-- Step 2: Authentication -->
					<div
						v-if="currentStep === 1"
						class="step-content"
					>
						<div class="d-flex flex-column">
							<Dropdown
								v-model="systemIntegration.authenticationTypeId"
								:items="authenticationOptions"
								label="Authentication Type *"
								rules="required"
								:disabled="!userFullPermissions"
								class="mb-4"
								style="width: 50%; max-width: 300px;"
								:explainer="authDescription"
							/>
						</div>
						<Checkbox
							v-if="(isEdit || cloneMode) && additionalAuthRequired"
							v-model="editConnectionDetails"
							label="Edit Connection Details"
							:disabled="!userFullPermissions"
							explainer="Enable this toggle to amend Integration specific connection credentials. All connection details need to be populated before saving any changes."
							class="mb-5"
						/>
						<v-row
							v-if="additionalAuthRequired && editConnectionDetails"
						>
							<v-col
								v-if="
									systemIntegration.authenticationTypeId === OAUTH2_USERNAME_AND_PASSWORD ||
										systemIntegration.authenticationTypeId === JWT
								"
								cols="12"
								md="6"
							>
								<TextField
									v-model="systemIntegration.username"
									label="Username *"
									rules="required"
									:disabled="!userFullPermissions"
								/>
							</v-col>
							<v-col
								v-if="systemIntegration.authenticationTypeId === OAUTH2_USERNAME_AND_PASSWORD"
								cols="12"
								md="6"
							>
								<PasswordField
									v-model="systemIntegration.password"
									label="Password *"
									:disabled="!userFullPermissions"
								/>
							</v-col>
							<v-col
								v-if="
									systemIntegration.authenticationTypeId === OAUTH2_USERNAME_AND_PASSWORD ||
										systemIntegration.authenticationTypeId === OAUTH2_CLIENT_CREDENTIALS ||
										systemIntegration.authenticationTypeId === JWT
								"
								cols="12"
								md="6"
							>
								<TextField
									v-model="systemIntegration.clientId"
									label="Client ID *"
									rules="required"
									explainer="Enter the Client ID for the target system"
									:disabled="!userFullPermissions"
								/>
							</v-col>
							<v-col
								v-if="
									systemIntegration.authenticationTypeId === OAUTH2_USERNAME_AND_PASSWORD ||
										systemIntegration.authenticationTypeId === OAUTH2_CLIENT_CREDENTIALS ||
										systemIntegration.authenticationTypeId === JWT
								"
								cols="12"
								md="6"
							>
								<TextField
									v-model="systemIntegration.clientSecret"
									:label="systemIntegration.authenticationTypeId === JWT ? 'Client Secret' : 'Client Secret *'"
									:rules="systemIntegration.authenticationTypeId === JWT ? '' : 'required'"
									:explainer="systemIntegration.authenticationTypeId === JWT ? 'Optional. Enter the Client Secret for the target system if required' : 'Enter the Client Secret for the target system'"
									:disabled="!userFullPermissions"
								/>
							</v-col>
							<v-col
								v-if="systemIntegration.authenticationTypeId === OAUTH2_CLIENT_CREDENTIALS"
								cols="12"
							>
								<Checkbox
									v-model="credentialViaHeader"
									label="Send client credentials in Authorization header"
									data-testid="credential-placement-checkbox"
									:disabled="!userFullPermissions"
									explainer="When enabled, the client ID and secret are sent in the request header. Leave unchecked to send them in the request body."
								/>
							</v-col>
							<v-col
								v-if="systemIntegration.authenticationTypeId === OAUTH2_USERNAME_AND_PASSWORD"
								cols="12"
								md="6"
							>
								<TextField
									v-model="systemIntegration.token"
									label="Token *"
									rules="required"
									explainer="Enter the token for the target system"
									:disabled="!userFullPermissions"
								/>
							</v-col>
							<v-col
								v-if="systemIntegration.authenticationTypeId === OAUTH2_CLIENT_CREDENTIALS"
								cols="12"
								md="6"
							>
								<TextField
									v-model="systemIntegration.auth2Scope"
									label="Auth2 Scope"
									explainer="Enter the Scope for the target system"
									:disabled="!userFullPermissions"
								/>
							</v-col>
							<v-col
								v-if="systemIntegration.authenticationTypeId === OAUTH2_CLIENT_CREDENTIALS"
								cols="12"
								md="6"
							>
								<TextField
									v-model="systemIntegration.audience"
									label="Audience"
									explainer="Optional. If your identity provider requires an audience to restrict access tokens, provide it here. Usually a URL like https://api.example.com"
									:disabled="!userFullPermissions"
								/>
							</v-col>
							<v-col
								v-if="systemIntegration.authenticationTypeId === JWT"
								cols="12"
								md="6"
							>
								<TextField
									v-model="systemIntegration.jwtCertKey"
									label="JWT Cert Key *"
									explainer="Enter the JWT Cert Key for the target system"
									rules="required"
									:disabled="!userFullPermissions"
								/>
							</v-col>
							<v-col
								v-if="systemIntegration.authenticationTypeId === OAUTH2_CLIENT_CREDENTIALS
									|| systemIntegration.authenticationTypeId === OAUTH2_USERNAME_AND_PASSWORD
									|| systemIntegration.authenticationTypeId === JWT"
								cols="12"
							>
								<TextField
									v-model="systemIntegration.authenticationUrl"
									label="Authentication URL *"
									rules="required"
									explainer="Enter the URL for the target system"
									:disabled="!userFullPermissions"
								/>
							</v-col>
						</v-row>
					</div>
				</div>
			</template>
			<template #modal-footer>
				<SecondaryActionButton
					class="mr-3"
					data-testid="cancel-button"
					@click="$emit('close')"
				>
					Cancel
				</SecondaryActionButton>
				<v-spacer />

				<!-- Step Navigation -->
				<SecondaryActionButton
					v-if="currentStep > 0"
					class="mr-3"
					@click="previousStep"
				>
					Previous
				</SecondaryActionButton>

				<PrimaryActionButton
					v-if="currentStep === 0"
					:disabled="!canProceedToAuthentication"
					@click="nextStep"
				>
					Next
				</PrimaryActionButton>

				<PrimaryActionButton
					v-if="currentStep === 1 && userFullPermissions"
					data-testid="save-button"
					@click="handleSubmit(submit)"
				>
					{{ buttonText }}
				</PrimaryActionButton>
			</template>
		</Modal>
	</ValidationForm>
</template>

<script>
/* eslint-disable max-lines */
import { mapGetters } from 'vuex'
import Modal from '../../../../../shared/components/modal.vue'
import IconButton from '../../../../../shared/components/icon-button.vue'
import SecondaryActionButton from '../../../../../shared/components/secondary-action-button.vue'
import PrimaryActionButton from '../../../../../shared/components/primary-action-button.vue'
import TextField from '../../../../../shared/components/text-field.vue'
import PasswordField from '../../../../../shared/components/password-field.vue'
import Dropdown from '../../../../../shared/components/dropdown.vue'
import TextArea from '../../../../../shared/components/textarea.vue'
import Checkbox from '../../../../../shared/components/checkbox.vue'
import ValidationForm from '../../../../../shared/components/validation-form.vue'
import { singleBrand, defaultBrandInSelect, clientMultiBranded, brandOptions, UNIVERSAL_BRAND, UNIVERSAL_BRAND_ITEM, userHasBrand } from '../../../../../shared/state/brands.js'
import { postSystemIntegration, putSystemIntegration } from '../../../../../shared/utils/api/connector-service.js'
import { showSnackbar } from '../../../../../shared/state/snackbar.js'
import { CONNECTOR_SERVICE_MODULE_FULL_PERMISSIONS } from '../../../../../shared/permissions/admin-portal-permissions.js'
import { generateSystemIntegrationDefaults } from '../../../../../shared/lib/system-integration-templates.js'

// OAuth2 client-credential placement lookup IDs (config.API_Integrations_CredentialPlacements).
const CREDENTIAL_PLACEMENT_BODY = 1
const CREDENTIAL_PLACEMENT_HEADER = 2

export default {
	name: 'manage-system-integration-modal',
	components: {
		Modal,
		IconButton,
		TextArea,
		Dropdown,
		TextField,
		PasswordField,
		PrimaryActionButton,
		SecondaryActionButton,
		Checkbox,
		ValidationForm
	},
	props: {
		systemIntegrationToEdit: {
			type: Object,
			default: null
		},
		lookupData: {
			type: Object,
			required: true
		},
		cloneMode: {
			type: Boolean,
			default: false
		},
		template: {
			type: Object,
			default: null
		},
		showBackButton: {
			type: Boolean,
			default: false
		}
	},
	setup () {
		return {
			brandOptions,
			clientMultiBranded,
			singleBrand,
			defaultBrandInSelect
		}
	},
	data () {
		const OAUTH2_USERNAME_AND_PASSWORD = 1
		const OAUTH2_CLIENT_CREDENTIALS = 2
		const JWT = 4
		const DIRECT_TO_S3 = 5

		return {
			currentStep: 0,
			systemIntegration: JSON.parse(JSON.stringify(this.systemIntegrationToEdit || this.getTemplateDefaults())),
			editConnectionDetails: true,
			OAUTH2_USERNAME_AND_PASSWORD,
			OAUTH2_CLIENT_CREDENTIALS,
			JWT,
			DIRECT_TO_S3
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		userFullPermissions () {
			if (this.isEdit) {
				return this.productAreaPermission(CONNECTOR_SERVICE_MODULE_FULL_PERMISSIONS) && this.systemIntegrationToEdit.isEditable && userHasBrand(this.systemIntegration.brandId)
			} else {
				return this.productAreaPermission(CONNECTOR_SERVICE_MODULE_FULL_PERMISSIONS)
			}
		},
		isEdit () {
			return !!this.systemIntegrationToEdit && !this.cloneMode
		},
		modalTitle () {
			const baseTitle = this.isEdit
				? 'Edit System'
				: this.cloneMode
					? 'Clone System'
					: 'Create System'

			const stepTitle = this.currentStep === 0 ? 'Details' : 'Authentication'
			return `${baseTitle} - ${stepTitle}`
		},
		buttonText () {
			if (this.isEdit) {
				return 'Save Changes'
			} else {
				return 'Create System'
			}
		},
		authenticationOptions () {
			return this.lookupData.authTypes.map(authType => ({
				text: authType.displayName,
				value: authType.id,
				displayOrder: authType.displayOrder,
				description: authType.description
			})).sort((a, b) => a.displayOrder - b.displayOrder)
		},
		authDescription () {
			return this.authenticationOptions.find(({ value }) => value === this.systemIntegration.authenticationTypeId)?.description
		},
		additionalAuthRequired () {
			return this.systemIntegration.authenticationTypeId === this.OAUTH2_USERNAME_AND_PASSWORD ||
				this.systemIntegration.authenticationTypeId === this.OAUTH2_CLIENT_CREDENTIALS ||
				this.systemIntegration.authenticationTypeId === this.JWT
		},
		filteredBrandOptions () {
			const containsUniversal = brandOptions.value.map(brand => {
				return brand.value
			}).includes(UNIVERSAL_BRAND)

			if (containsUniversal || !this.isEdit || this.cloneMode) {
				return brandOptions.value
			} else {
				return [
					UNIVERSAL_BRAND_ITEM,
					...brandOptions.value
				]
			}
		},
		canProceedToAuthentication () {
			// Validate required fields for step 1 (endpoint may be optional for Direct to S3)
			return this.systemIntegration.integrationSystemName &&
				this.systemIntegration.frequencyInSeconds >= 1
		},
		credentialViaHeader: {
			// Bridges the boolean checkbox to the integer placement lookup ID.
			get () {
				return this.systemIntegration.credentialPlacementId === CREDENTIAL_PLACEMENT_HEADER
			},
			set (value) {
				this.systemIntegration.credentialPlacementId = value ? CREDENTIAL_PLACEMENT_HEADER : CREDENTIAL_PLACEMENT_BODY
			}
		},
		endpointAddressRules () {
			// Only require endpoint when auth type is selected and is NOT Direct to S3
			if (this.systemIntegration.authenticationTypeId && this.systemIntegration.authenticationTypeId !== this.DIRECT_TO_S3) {
				return 'required'
			}
			return ''
		}
	},
	created () {
		if (this.isEdit || this.cloneMode) {
			this.editConnectionDetails = false
		}
		if (this.cloneMode) {
			this.systemIntegration.integrationSystemName += ' (clone)'
			this.systemIntegration.isEditable = true
			this.systemIntegration.cloneFromId = this.systemIntegration.id
			delete this.systemIntegration.id
			this.systemIntegration.enabled = false
		}

		// Normalize JWT clientSecret on load to prevent backend issues
		// JWT provider requires empty string, not null, for missing clientSecret
		if (this.systemIntegration.authenticationTypeId === this.JWT && !this.systemIntegration.clientSecret) {
			this.systemIntegration.clientSecret = ''
		}

		// Existing integrations that predate this setting have no placement — default them to BODY.
		// Use $set: in Vue 2 a plain assignment of a previously-absent property is not reactive,
		// which would break the checkbox v-model / credentialViaHeader computed.
		if (!this.systemIntegration.credentialPlacementId) {
			this.$set(this.systemIntegration, 'credentialPlacementId', CREDENTIAL_PLACEMENT_BODY)
		}
	},
	methods: {
		getTemplateDefaults () {
			const baseDefaults = {
				integrationSystemName: null,
				endpointAddress: null,
				authenticationTypeId: null,
				clientId: null,
				clientSecret: null,
				username: null,
				password: null,
				validFrom: null,
				validTo: null,
				token: null,
				notes: null,
				enabled: false,
				brandId: 0,
				frequencyInSeconds: 10, // 10 seconds default
				auth2Scope: null,
				authenticationUrl: null,
				jwtCertKey: null,
				resource: null,
				audience: null,
				templateGUID: null,
				// New integrations default to sending credentials via the Authorization header.
				credentialPlacementId: CREDENTIAL_PLACEMENT_HEADER
			}

			return generateSystemIntegrationDefaults(this.template, baseDefaults)
		},
		nextStep () {
			if (this.currentStep < 1 && this.canProceedToAuthentication) {
				this.currentStep++
			}
		},
		previousStep () {
			if (this.currentStep > 0) {
				this.currentStep--
			}
		},
		handleBack () {
			if (this.currentStep > 0) {
				this.previousStep()
			} else if (this.showBackButton) {
				this.$emit('back')
			}
		},
		async submit () {
			try {
				// Validation: endpointAddress can only be null if authenticationTypeId === DirectToS3 (5)
				const requiresEndpoint = this.systemIntegration.authenticationTypeId !== this.DIRECT_TO_S3
				const endpointMissing = !this.systemIntegration.endpointAddress || !String(this.systemIntegration.endpointAddress).trim()
				if (requiresEndpoint && endpointMissing) {
					showSnackbar({ text: 'API Base URL is required for the selected authentication type. Please enter a value on the Details step.', color: 'red' })
					this.currentStep = 0
					return
				}

				// For JWT authentication, ensure clientSecret is an empty string if not provided (rather than null)
				// This normalization must happen regardless of editConnectionDetails to prevent null values from breaking JWT provider
				if (this.systemIntegration.authenticationTypeId === this.JWT && !this.systemIntegration.clientSecret) {
					this.systemIntegration.clientSecret = ''
				}

				if (!this.isEdit || this.cloneMode) {
					if (this.cloneMode) {
						this.systemIntegration.cloneAuthFields = !this.editConnectionDetails
					}
					await postSystemIntegration(this.systemIntegration)
					showSnackbar('System created successfully')
					this.$emit('system-created', this.systemIntegration)
				} else {
					this.systemIntegration.editAuthFields = this.editConnectionDetails
					await putSystemIntegration(this.systemIntegration)
					showSnackbar('System successfully updated')
					this.$emit('system-updated', this.systemIntegration)
				}
				this.$emit('close')
			} catch (error) {
				console.error('Error saving system:', error)
				showSnackbar(`Failed to ${this.isEdit ? 'update' : 'create'} system`, 'error')
			}
		},
		updateSystemIntegration (property, value) {
			this.systemIntegration[property] = value
		},
		getValue (property) {
			return this.systemIntegration[property]
		}
	}
}
</script>

<style scoped>
/* Container to constrain form width within modal */
.form-container {
	max-width: 100%;
	margin: 0 auto;
}

/* Step Progress Styles */
.step-progress-container {
	width: 100%;
}

.step-title {
	font-size: 1.2rem;
	font-weight: 500;
	color: var(--syrenis-black);
	margin-bottom: 16px;
}

.step-progress-bar {
	width: 100%;
	height: 4px;
	background-color: var(--global-border-color);
	border-radius: var(--radius-xs, 2px);
	overflow: hidden;
	margin-bottom: 12px;
}

.step-progress-fill {
	height: 100%;
	background-color: var(--global-primary, var(--global-color-blue-darken-2));
	transition: width 0.3s ease;
}

.step-content {
	min-height: 400px; /* Prevents layout shift between steps */
	animation: fadeIn 0.3s ease-in-out;
}

/* Animations */
@keyframes fadeIn {
	from {
		opacity: 0;
		transform: translateX(20px);
	}
	to {
		opacity: 1;
		transform: translateX(0);
	}
}

/* Responsive adjustments for modal content */
@media (max-width: 768px) {
	.form-container {
		padding: 0 8px;
	}
}
</style>
